import * as React from 'react';
import styled from 'styled-components';
import { rem, darken } from 'polished';
import { Link } from 'react-router';
import { __ } from 'react-i18n/lib';
import {
  FlexRowCenterVertical,
  FlexColCenter,
} from '../../Defaults/Flexbox/Flexbox';
import { TextP } from '../../Defaults/Text';
import { prop, getSymbolFromCurrency } from '../../../utilities';
import { ProductSearchItem } from '../Product/ProductSearchItem';
import { ZemplinLoaderWrapper } from '../../..';
import {
  getSearchProductInfo,
  getSearchCategoryInfo,
} from '../../../utilities/selectors';
import { NoItemsWrapper } from '../General/NoItemsWrapper';
import { Loader } from '../General/Loader';

// Interfaces

interface SearchResultsViewerProps {
  visible: boolean;
  searchResults: {
    productsResult: any;
    categoriesResult: any;
  };
  searchTerm: string;
  redirectToResultPage: (e) => void;
  hideResultViewer: () => void;
  innerRef?: any;
  partialSearchProductsIsFetching: boolean;
  partialSearchCategoriesIsFetching: boolean;
}

/*****************/

// Styled Components

const SearchDropdown = styled.div<{ visible: boolean }>`
  display: ${({ visible }) => (visible ? 'block' : 'none')};
  background: ${({ theme }) => theme.color.white};
  color: ${({ theme }) => theme.color.text};
  border-radius: 0 0 ${rem(2)} ${rem(2)};
  width: ${rem(450)};
  max-height: ${rem(480)};
  overflow-y: auto;
  position: absolute;
  top: ${rem(44)};
  width: 100%;
  z-index: 500;
  left: unset;

  transform: translateY(${rem(5)});
  box-shadow: ${({ theme }) => theme.boxShadow.secondary};
`;

const NoResultsSearchDropdown = styled(SearchDropdown)`
  padding: ${rem(24)};
`;

const DropdownSection = styled.div`
  padding: ${rem(24)} ${rem(16)} ${rem(8)} ${rem(16)};
`;

const ProductDropdownSection = styled(DropdownSection)`
  padding-top: ${rem(24)};
  padding-bottom: ${rem(8)};
`;

const DropdownTitle = styled.p`
  color: ${({ theme }) => theme.colors.placeholder};
  font-family: ${({ theme }) => theme.font.primary};
  font-size: ${rem(12)};
  margin: 0;
  padding-bottom: ${rem(16)};
`;

const DropdownShowAllProducts = styled(Link)`
  display: block;
  background: transparent;
  border-radius: 0 0 0 ${rem(2)};
  color: ${({ theme }) => theme.colors.primary};
  font-size: 14px;
  line-height: 1;
  text-align: center;
  padding: ${rem(19)} ${rem(10)};
  margin-top: ${rem(11)};

  ${({ theme }) => theme.media(0, 1024)} {
    padding: ${rem(14)} ${rem(10)};
  }

  &:hover {
    cursor: pointer;
    color: white;
    background: ${({ theme }) => theme.colors.primary};
  }
`;

const DropdownName = styled(TextP)`
  font-size: ${rem(14)};
`;

const DropdownItem = styled(Link)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  text-decoration: none;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-top: ${rem(14)};

  &:hover > ${DropdownName} {
    text-decoration: underline;
  }
`;

const NumberOfProducts = styled(TextP)`
  font-size: ${rem(14)};
  color: ${({ theme }) => theme.colors.placeholder};
`;

const HighlightedText = styled.span`
  background-color: ${({ theme }) => theme.colors.highlight};
`;

const Bold = styled.span`
  font-weight: 400;
  font-size: ${rem(18)};
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const ProductTitleWrapper = styled(FlexRowCenterVertical)`
  justify-content: space-between;
  border-bottom: ${rem(1)} solid rgb(49, 39, 131, 0.08);
`;

const NoResults = styled(TextP)`
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-top: ${rem(14)};
  word-break: break-word;
`;

const ProductResultsWrapper = styled(FlexColCenter)`
  padding-top: ${rem(24)};
`;

/*****************/

class SearchResultsViewer extends React.Component<SearchResultsViewerProps> {
  public render() {
    const {
      visible,
      partialSearchProductsIsFetching,
      partialSearchCategoriesIsFetching,
      searchResults,
      redirectToResultPage,
      innerRef,
      searchTerm,
    } = this.props;

    if (searchTerm.length < 2) {
      return null;
    }
    const productsResult = prop(searchResults, 'productsResult', null);
    const categories = prop(searchResults, 'categoriesResult', []);
    const onlyTopTwoCat = categories && categories.slice(0, 2);

    if (
      !partialSearchProductsIsFetching &&
      !partialSearchCategoriesIsFetching &&
      (!onlyTopTwoCat || onlyTopTwoCat.length === 0) &&
      (!productsResult ||
        !productsResult.products ||
        (productsResult &&
          productsResult.products &&
          productsResult.products.length === 0))
    ) {
      return (
        <NoResultsSearchDropdown visible={visible}>
          <DropdownSection>
            <Bold>{__('Žiadne dostupné výsledky vyhľadávania')}...</Bold>
          </DropdownSection>
        </NoResultsSearchDropdown>
      );
    }

    return (
      <SearchDropdown visible={visible} innerRef={innerRef}>
        {onlyTopTwoCat && onlyTopTwoCat.length > 0 && (
          <DropdownSection>
            <ProductTitleWrapper>
              <DropdownTitle>{__('Kategórie')}</DropdownTitle>
            </ProductTitleWrapper>
            {this.printCategoriesResults()}
          </DropdownSection>
        )}
        {productsResult &&
          productsResult.products &&
          productsResult.products.length > 0 && (
            <ProductDropdownSection>
              <ProductTitleWrapper>
                <DropdownTitle>{__('Produkty')}</DropdownTitle>
                <DropdownTitle>{__('Bez DPH')}</DropdownTitle>
              </ProductTitleWrapper>
              {this.printProductResults()}
            </ProductDropdownSection>
          )}
        <DropdownShowAllProducts to="" onClick={redirectToResultPage}>
          {__('Zobraziť všetko')}
        </DropdownShowAllProducts>
      </SearchDropdown>
    );
  }

  private printCategoriesResults = () => {
    const {
      searchTerm,
      partialSearchCategoriesIsFetching,
      searchResults,
      hideResultViewer,
    } = this.props;

    if (partialSearchCategoriesIsFetching) {
      return <Loader small={true} />;
    }

    const categories = prop(searchResults, 'categoriesResult', []);
    const onlyTopTwoCat = categories && categories.slice(0, 2);

    return (
      <>
        {onlyTopTwoCat &&
          onlyTopTwoCat.map(category => {
            const { name, url } = getSearchCategoryInfo(category);
            const numOfProducts = 250;
            const parts = name.split(new RegExp(`(${searchTerm})`, 'gi'));

            return (
              <DropdownItem
                onClick={hideResultViewer}
                to={`/${url}`}
                key={name}
              >
                <DropdownName>
                  {parts &&
                    parts.map((part, i) =>
                      part.toLowerCase() === searchTerm.toLowerCase() ? (
                        <HighlightedText key={i}>{part}</HighlightedText>
                      ) : (
                        part
                      ),
                    )}
                </DropdownName>
                <NumberOfProducts>{numOfProducts}</NumberOfProducts>
              </DropdownItem>
            );
          })}
      </>
    );
  };

  private printProductResults = () => {
    const {
      searchTerm,
      searchResults,
      partialSearchProductsIsFetching,
      hideResultViewer,
    } = this.props;

    if (partialSearchProductsIsFetching) {
      return <Loader small={true} />;
    }

    const productsResult = prop(searchResults, 'productsResult', []);

    const { products } = productsResult;

    return (
      <ProductResultsWrapper>
        {products.length !== 0 ? (
          <>
            {products.map(product => {
              const {
                id,
                picture,
                url,
                name,
                price,
                unit,
                currency,
              } = getSearchProductInfo(product);

              return (
                <ProductSearchItem
                  onClick={hideResultViewer}
                  searchTerm={searchTerm}
                  url={url}
                  name={name}
                  currency={getSymbolFromCurrency(currency)}
                  price={price}
                  key={id}
                  imgSrc={picture}
                  unit={unit}
                />
              );
            })}
          </>
        ) : (
          <NoResults>{`${__('Pre výraz')}: "${searchTerm}" ${__(
            'sa nenašli žiadne výsledky',
          )}`}</NoResults>
        )}
      </ProductResultsWrapper>
    );
  };
}

export default SearchResultsViewer;
